import { t, type I18nLocale } from '@ardoq/i18n';
import type { PptxExportState } from './pptxExport$';

type RunningExport = Exclude<PptxExportState, { status: 'idle' }>;

export type ExportOverlayProgress = {
  percentValue: number;
  valueNow: number;
  valueMax: number;
  label: string;
  valueText: string;
};

export type ExportOverlayProps = {
  heading: string;
  slideTitle: string | null;
  loaderAriaLabel: string;
  progress: ExportOverlayProgress;
  cancelLabel: string;
  canCancel: boolean;
};

const toHeading = (state: RunningExport, i18nLocale: I18nLocale): string => {
  if (state.status === 'building') {
    return t(
      i18nLocale,
      'presentationAdmin:viewer.exportOverlay.building',
      'Building PPTX…'
    );
  }
  return t(
    i18nLocale,
    'presentationAdmin:viewer.exportOverlay.exportingSlide',
    { current: state.slideNumber, total: state.total },
    'Exporting slide {{current}} of {{total}}'
  );
};

const toCompletedSlides = (state: RunningExport): number =>
  state.status === 'capturing' ? state.slideNumber - 1 : state.total;

const toSlideTitle = (state: RunningExport): string | null =>
  state.status === 'capturing' && state.title ? state.title : null;

const toProgress = (
  state: RunningExport,
  i18nLocale: I18nLocale
): ExportOverlayProgress => {
  const completed = toCompletedSlides(state);
  return {
    percentValue: Math.round((completed / state.total) * 100),
    valueNow: completed,
    valueMax: state.total,
    label: t(
      i18nLocale,
      'presentationAdmin:viewer.exportOverlay.progressAriaLabel',
      'PPTX export progress'
    ),
    valueText: t(
      i18nLocale,
      'presentationAdmin:viewer.exportOverlay.progress',
      { completed, total: state.total },
      'Slides done: {{completed}} of {{total}}'
    ),
  };
};

export const toExportOverlayProps = (
  state: PptxExportState,
  i18nLocale: I18nLocale
): ExportOverlayProps | null => {
  if (state.status === 'idle') {
    return null;
  }
  return {
    heading: toHeading(state, i18nLocale),
    slideTitle: toSlideTitle(state),
    loaderAriaLabel: t(
      i18nLocale,
      'presentationAdmin:viewer.exportOverlay.loaderAriaLabel',
      'Exporting'
    ),
    progress: toProgress(state, i18nLocale),
    cancelLabel: t(
      i18nLocale,
      'presentationAdmin:viewer.exportOverlay.cancel',
      'Cancel'
    ),
    canCancel: state.status === 'capturing',
  };
};
